/*
  Consent state for the site's tracking categories.

  ── What this file is, and what it is not ──────────────────────────────────
  This is the seam between "the visitor made a choice" and the code that acts
  on that choice. It renders no UI, asks nobody anything and decides nothing:
  it stores what it is told and tells whoever is listening.

  It is NOT a consent UI and it is NOT a consent-management platform. Today
  nothing on the site asks the question: it used to be a checkbox in the Join
  the Waitlist dialog, which has been retired, and there is no consent banner.
  If a DPDP-compliant banner or a vendor widget is added later,
  the only thing it has to do to switch Meta tracking on is call
  setMarketingConsent(true) for an explicit accept and setMarketingConsent(false)
  for an explicit reject. Nothing else about the Meta implementation needs to
  change, and nothing else may be treated as consent. See
  CONSENT_INTEGRATION.md.

  ── The default is no ──────────────────────────────────────────────────────
  hasMarketingConsent() returns false for every state that is not an explicit
  stored accept:

    - a first-time visitor, with nothing stored;
    - a visitor who scrolled past the banner without choosing;
    - an explicit reject;
    - a private window, or a browser that blocks site data, or a storage error
      — the read throws and is treated as "no";
    - a stored record from a different CONSENT_VERSION, i.e. one written
      against an older set of categories or an older disclosure. A changed
      version invalidates the old answer rather than inheriting it.

  So "we do not know" and "no" take the same path: the Meta Pixel is never
  loaded and no Meta event is ever sent. That property holds by construction
  rather than by every call site remembering to check.

  ── Categories ─────────────────────────────────────────────────────────────
  Only `marketing` exists here, because only marketing tracking is currently
  implemented. Meta Pixel and Meta's Conversions API are both advertising
  tracking and both sit behind this one category. Add a category when there is
  something behind it, not before.
*/

/* Bump when the categories, or the disclosure they were accepted against,
   change: a stored answer to a different question is not an answer. */
export const CONSENT_VERSION = 1;

/* localStorage rather than a cookie, on purpose. A cookie is sent with every
   request to the origin, which would make the consent record itself a piece of
   ambient tracking. Nothing server-side reads this value. */
export const CONSENT_STORAGE_KEY = "sm_consent_v1";

/** The category name Meta tracking is gated on. One string, one meaning. */
export const MARKETING_CATEGORY = "marketing";

/* Every state that is not an explicit accept. `decided` separates "said no"
   from "never asked", which is what a banner needs in order to know whether to
   show itself. Neither one grants anything. */
const DENIED = Object.freeze({ marketing: false, decided: false });

const listeners = new Set();
let storageListenerAttached = false;

function storage() {
  /* Touching window.localStorage itself throws in a browser configured to
     block site data, so even the lookup is guarded. */
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * The stored consent record, or DENIED.
 *
 * Never throws and never returns a partial object: anything unreadable,
 * unparseable, of the wrong shape or of the wrong version reads as DENIED.
 */
export function readConsent() {
  const store = storage();
  if (!store) return DENIED;

  let raw;
  try {
    raw = store.getItem(CONSENT_STORAGE_KEY);
  } catch {
    return DENIED;
  }
  if (!raw) return DENIED;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return DENIED;
    if (parsed.version !== CONSENT_VERSION) return DENIED;
    return Object.freeze({
      /* Strictly true, not truthy. A stored "yes", a 1 or an object is a
         corrupt record, not a grant. */
      marketing: parsed[MARKETING_CATEGORY] === true,
      decided: true,
      decidedAt: typeof parsed.decidedAt === "string" ? parsed.decidedAt : undefined,
    });
  } catch {
    return DENIED;
  }
}

/**
 * True only when the visitor has explicitly accepted marketing/advertising
 * tracking. This is the single question every Meta call site asks.
 */
export function hasMarketingConsent() {
  return readConsent().marketing === true;
}

/**
 * Whether the visitor has answered at all — for the banner to decide whether
 * to show itself. Not a grant of anything.
 */
export function hasConsentDecision() {
  return readConsent().decided === true;
}

function notify() {
  const state = readConsent();
  /* Iterate a copy, so a listener that unsubscribes inside its own callback
     cannot mutate the set being walked. */
  for (const listener of Array.from(listeners)) {
    try {
      listener(state);
    } catch {
      /* One broken listener must not stop the others, and must not be able to
         leave the pixel half-configured. */
    }
  }
}

/**
 * Record the visitor's marketing/advertising choice.
 *
 * This is the only way consent is ever granted. `true` must mean an unambiguous affirmative
 * action — a click that ticks the box, carried through a submit — and nothing
 * else: not a dismissal, not a scroll, not a timeout, not a pre-ticked box.
 *
 * @param {boolean} granted
 * @returns {boolean} the value now in force.
 */
export function setMarketingConsent(granted) {
  const marketing = granted === true;
  const store = storage();

  if (store) {
    try {
      store.setItem(
        CONSENT_STORAGE_KEY,
        JSON.stringify({
          version: CONSENT_VERSION,
          [MARKETING_CATEGORY]: marketing,
          decidedAt: new Date().toISOString(),
        })
      );
    } catch {
      /* Storage full or blocked. The choice cannot be remembered for the next
         visit, which means the next visit reads as DENIED — the safe way to
         fail. The listeners below still run, so this page behaves as chosen. */
    }
  }

  notify();
  return marketing;
}

/**
 * Forget the stored choice, returning the visitor to "never asked".
 *
 * For a "review my cookie choices" control: after this the banner should ask
 * again and, until it is answered, nothing is granted. Listeners see a denial,
 * so tracking stops the same way an explicit reject stops it.
 */
export function clearConsent() {
  const store = storage();
  if (store) {
    try {
      store.removeItem(CONSENT_STORAGE_KEY);
    } catch {
      /* Nothing to do. The read path treats an unreadable record as DENIED. */
    }
  }
  notify();
}

/**
 * Listen for consent changes.
 *
 * Called on every set/clear in this tab and — via the `storage` event — when
 * another tab of the same origin changes the record, so a visitor who accepts
 * in one tab is not left with a half-tracked second tab. The listener receives
 * the whole state and should read it rather than assume a direction.
 *
 * @param {(state: {marketing: boolean, decided: boolean}) => void} listener
 * @returns {() => void} unsubscribe
 */
export function subscribeToConsent(listener) {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);

  if (!storageListenerAttached && typeof window !== "undefined") {
    storageListenerAttached = true;
    window.addEventListener("storage", (event) => {
      // key === null is a storage.clear() from another tab.
      if (event.key === null || event.key === CONSENT_STORAGE_KEY) notify();
    });
  }

  return () => listeners.delete(listener);
}

/**
 * Run `callback` once marketing consent is in force — immediately if it
 * already is, otherwise on the grant.
 *
 * For a tracking call that describes a state the visitor is still in, rather
 * than a moment that has passed. The launch page's ViewContent is that case:
 * the page is still on screen when the banner is answered, so the event is
 * about now and not a replay of something that happened before consent. A
 * past action — a click, a submitted form — must never be routed through
 * here; see the no-replay note in tracking.js.
 *
 * Fires at most once, and unsubscribes itself.
 *
 * @returns {() => void} unsubscribe, suitable as a useEffect cleanup.
 */
export function onMarketingConsent(callback) {
  if (typeof callback !== "function") return () => {};

  if (hasMarketingConsent()) {
    callback();
    return () => {};
  }

  let done = false;
  const unsubscribe = subscribeToConsent((state) => {
    if (done || !state.marketing) return;
    done = true;
    unsubscribe();
    callback();
  });
  return unsubscribe;
}

/** Test-only. Clears the stored record and every listener. */
export function resetConsentForTests() {
  listeners.clear();
  const store = storage();
  try {
    store?.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    /* Nothing stored is the state the tests want anyway. */
  }
}
